import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { humanise } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, useToast } from '../components/ui';

interface CategoryNode {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  path: string;
  sector: string;
  listingType: string;
  icon: string | null;
  sortOrder: number;
  showInHeader: boolean;
  headerOrder: number | null;
  children?: CategoryNode[];
}

const SECTORS = [
  'CRM', 'BUY_SELL', 'REAL_ESTATE', 'LOCAL_SERVICE', 'PACKERS_MOVERS', 'TRAVELS',
  'HOME_SERVICE', 'RENTALS', 'JOBS', 'EDUCATION', 'EVENTS', 'CATERING',
];
const LISTING_TYPES = [
  'PRODUCT', 'PROPERTY', 'SERVICE', 'RENTAL', 'JOB_AVAILABLE',
  'EDUCATION', 'TRAVEL', 'PACKERS_MOVERS', 'EVENT',
];

/** v2 §3/§37/§38 — the tree is data, so adding a category needs no app release. */
export function CategoriesPage() {
  const [editing, setEditing] = useState<{ row?: CategoryNode; parentId?: string | null } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: () => api.get<CategoryNode[]>('/categories?tree=true'),
  });

  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id ? api.patch(`/categories/${id}`, body) : api.post('/categories', body),
    onSuccess: () => {
      toast('Category saved', 'success');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['categories-tree'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const toggle = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const rows = useMemo(() => {
    const out: Array<{ node: CategoryNode; depth: number }> = [];
    const walk = (nodes: CategoryNode[], depth: number) => {
      for (const node of nodes) {
        out.push({ node, depth });
        if (expanded.has(node.id) && node.children?.length) walk(node.children, depth + 1);
      }
    };
    walk(data ?? [], 0);
    return out;
  }, [data, expanded]);

  return (
    <div className="stack">
      <Notice tone="info" title="Adding a category does not need an app release">
        The seven header categories are top-level entries flagged “show in header”. Everything the app renders — the
        header row, sub-category screens, the sell flow — reads this tree.
      </Notice>

      <Card flush>
        <CardHead
          title="Category tree"
          action={
            <Button variant="primary" size="sm" onClick={() => setEditing({ parentId: null })}>
              + Top-level category
            </Button>
          }
        />
        {isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 200 }} />
          </div>
        ) : !rows.length ? (
          <Empty icon="⌗" title="No categories yet" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Sector</th>
                  <th>Listing type</th>
                  <th>Header</th>
                  <th>Path</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ node, depth }) => (
                  <tr key={node.id}>
                    <td>
                      <div className="row" style={{ paddingLeft: depth * 22, gap: 7 }}>
                        {node.children?.length ? (
                          <button
                            onClick={() => toggle(node.id)}
                            style={{ border: 'none', background: 'none', color: 'var(--ink-faint)', width: 16 }}
                          >
                            {expanded.has(node.id) ? '▾' : '▸'}
                          </button>
                        ) : (
                          <span style={{ width: 16 }} />
                        )}
                        <span className="cell-strong">{node.name}</span>
                        {node.children?.length ? (
                          <span className="cell-muted">({node.children.length})</span>
                        ) : null}
                      </div>
                    </td>
                    <td>{humanise(node.sector)}</td>
                    <td className="cell-muted">{humanise(node.listingType)}</td>
                    <td>
                      {node.showInHeader ? <Badge tone="blue">#{node.headerOrder}</Badge> : <span className="cell-muted">—</span>}
                    </td>
                    <td className="cell-mono cell-clip">{node.path}</td>
                    <td>
                      <div className="cell-actions">
                        <Button size="sm" onClick={() => setEditing({ parentId: node.id })}>
                          + Child
                        </Button>
                        <Button size="sm" onClick={() => setEditing({ row: node })}>
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <CategoryEditor
          editing={editing}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(body) => save.mutate({ id: editing.row?.id, body })}
        />
      )}
    </div>
  );
}

function CategoryEditor({
  editing,
  onClose,
  onSave,
  saving,
}: {
  editing: { row?: CategoryNode; parentId?: string | null };
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const row = editing.row;
  const [form, setForm] = useState({
    name: row?.name ?? '',
    sector: row?.sector ?? 'BUY_SELL',
    listingType: row?.listingType ?? 'PRODUCT',
    icon: row?.icon ?? '',
    sortOrder: String(row?.sortOrder ?? 0),
    showInHeader: row?.showInHeader ? 'true' : 'false',
    headerOrder: String(row?.headerOrder ?? ''),
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  function submit() {
    const base: Record<string, unknown> = {
      name: form.name.trim(),
      icon: form.icon.trim() || undefined,
      sortOrder: Number(form.sortOrder) || 0,
      showInHeader: form.showInHeader === 'true',
      headerOrder: form.headerOrder ? Number(form.headerOrder) : undefined,
    };
    // Sector and type are fixed at creation: changing them on a live category
    // would silently reclassify every listing already filed under it.
    if (!row) {
      base.sector = form.sector;
      base.listingType = form.listingType;
      if (editing.parentId) base.parentId = editing.parentId;
    }
    onSave(base);
  }

  return (
    <Modal
      title={row ? `Edit “${row.name}”` : 'New category'}
      sub={row ? 'Renaming updates the path of every category beneath it.' : undefined}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!form.name.trim()} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <Input value={form.name} autoFocus onChange={(e) => set('name', e.target.value)} />
        </Field>

        {!row && (
          <div className="row">
            <Field label="Sector">
              <Select
                value={form.sector}
                onChange={(e) => set('sector', e.target.value)}
                options={SECTORS.map((s) => ({ value: s, label: humanise(s) }))}
              />
            </Field>
            <Field label="Listing type">
              <Select
                value={form.listingType}
                onChange={(e) => set('listingType', e.target.value)}
                options={LISTING_TYPES.map((s) => ({ value: s, label: humanise(s) }))}
              />
            </Field>
          </div>
        )}

        <div className="row">
          <Field label="Icon" hint="Material icon name, as used by the app.">
            <Input value={form.icon} onChange={(e) => set('icon', e.target.value)} placeholder="sell" />
          </Field>
          <Field label="Sort order">
            <Input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
          </Field>
        </div>

        <div className="row">
          <Field label="Show in header" hint="The seven-category row at the top of the app.">
            <Select
              value={form.showInHeader}
              onChange={(e) => set('showInHeader', e.target.value)}
              options={[
                { value: 'false', label: 'No' },
                { value: 'true', label: 'Yes' },
              ]}
            />
          </Field>
          {form.showInHeader === 'true' && (
            <Field label="Header position">
              <Input type="number" value={form.headerOrder} onChange={(e) => set('headerOrder', e.target.value)} />
            </Field>
          )}
        </div>
      </div>
    </Modal>
  );
}
